// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * The Claude-check A/B: what Claude Haiku said when it was shown the unclear analysis candidates
 * (geodata's CandidateCheckService), held against what people, OSM and GPS say is there, and
 * what applying its verdicts at a delta would move across the serving floor (geodata's
 * ClaudeCheckAb). Read-only; nothing reaches players until ClaudeCheckApplies is switched on.
 */

export type ClaudeCheckSettings = {
  enabled: boolean;
  applies: boolean;
  model: string;
  delta: number;
  minConfidence: number;
  maxConfidence: number;
  kinds: string[];
  dailyLimit: number;
};

/** One kind's verdicts. Every number is geodata's; the page only shows them. */
export type ClaudeCheckKind = {
  kind: string;
  yes: number;
  no: number;
  unsure: number;
  failed: number;
  noPicture: number;
  yesReal: number;
  yesFalse: number;
  noReal: number;
  noFalse: number;
  unsureReal: number;
  unsureFalse: number;
  /** Would cross the floor upwards if verdicts applied at the delta. */
  lifted: number;
  liftedReal: number;
  liftedFalse: number;
  /** Would fall under the floor if verdicts applied at the delta. */
  dropped: number;
  droppedReal: number;
  droppedFalse: number;
};

/** One verdict as Claude gave it. */
export type ClaudeCheckRecent = {
  courseId: string;
  courseName: string | null;
  holeNumber: number;
  kind: string;
  verdict: string;
  seenAs: string | null;
  confidence: number | null;
  reason: string;
  candidateConfidence: number;
  /** real, false or unknown: what people, OSM or GPS say the candidate is. */
  truth: string;
  checkedAt: string | null;
};

export type ClaudeCheckAb = {
  settings: ClaudeCheckSettings;
  delta: number;
  servedFloor: number;
  sentToday: number;
  inputTokens: number;
  outputTokens: number;
  courses: number;
  kinds: ClaudeCheckKind[];
  recent: ClaudeCheckRecent[];
};

type Row = Record<string, unknown>;

/** A field by its camelCase name, or the PascalCase one a differently configured serializer writes. */
const pick = (row: Row, key: string): unknown => row[key] ?? row[key.charAt(0).toUpperCase() + key.slice(1)];

const num = (row: Row, key: string): number => {
  const value = pick(row, key);
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const numOrNull = (row: Row, key: string): number | null => {
  const value = pick(row, key);
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const str = (row: Row, key: string): string | null => {
  const value = pick(row, key);
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const rows = (row: Row, key: string): Row[] => {
  const value = pick(row, key);
  return Array.isArray(value) ? value.filter((v): v is Row => typeof v === 'object' && v !== null) : [];
};

const KIND_COUNTS = [
  'yes',
  'no',
  'unsure',
  'failed',
  'noPicture',
  'yesReal',
  'yesFalse',
  'noReal',
  'noFalse',
  'unsureReal',
  'unsureFalse',
  'lifted',
  'liftedReal',
  'liftedFalse',
  'dropped',
  'droppedReal',
  'droppedFalse',
] as const;

/** Maps the BFF's `/api/geodata/ml/claude-check` body. Exported for direct unit testing. */
export const mapClaudeCheckAb = (body: unknown): ClaudeCheckAb | null => {
  if (typeof body !== 'object' || body === null) return null;
  const row = body as Row;
  const settingsRaw = pick(row, 'settings');
  const settings = (typeof settingsRaw === 'object' && settingsRaw !== null ? settingsRaw : {}) as Row;
  const kinds = pick(settings, 'kinds');
  return {
    settings: {
      enabled: pick(settings, 'enabled') === true,
      applies: pick(settings, 'applies') === true,
      model: str(settings, 'model') ?? '',
      delta: num(settings, 'delta'),
      minConfidence: num(settings, 'minConfidence'),
      maxConfidence: num(settings, 'maxConfidence'),
      kinds: Array.isArray(kinds) ? kinds.filter((k): k is string => typeof k === 'string') : [],
      dailyLimit: num(settings, 'dailyLimit'),
    },
    delta: num(row, 'delta'),
    servedFloor: num(row, 'minServedConfidence'),
    sentToday: num(row, 'sentToday'),
    inputTokens: num(row, 'inputTokens'),
    outputTokens: num(row, 'outputTokens'),
    courses: num(row, 'courses'),
    kinds: rows(row, 'kinds')
      .map((k) => {
        const counts = Object.fromEntries(KIND_COUNTS.map((key) => [key, num(k, key)])) as Record<
          (typeof KIND_COUNTS)[number],
          number
        >;
        return { kind: str(k, 'kind') ?? '', ...counts };
      })
      .filter((k) => k.kind.length > 0),
    recent: rows(row, 'recent')
      .map((r) => ({
        courseId: str(r, 'courseId') ?? '',
        courseName: str(r, 'courseName'),
        holeNumber: num(r, 'holeNumber'),
        kind: str(r, 'kind') ?? '',
        verdict: str(r, 'verdict') ?? '',
        seenAs: str(r, 'seenAs'),
        confidence: numOrNull(r, 'confidence'),
        reason: str(r, 'reason') ?? '',
        candidateConfidence: num(r, 'candidateConfidence'),
        truth: str(r, 'truth') ?? 'unknown',
        checkedAt: str(r, 'checkedAt'),
      }))
      .filter((r) => r.courseId.length > 0 && r.verdict.length > 0),
  };
};

export type ClaudeCheckState = {
  ab: ClaudeCheckAb | null;
  loading: boolean;
  error: 'forbidden' | 'unavailable' | 'failed' | null;
};

/** Reads the comparison at verdict strength `delta`; null means geodata's configured one. */
export function useClaudeCheck(
  apiBaseUrl: string,
  token: string | null | undefined,
  enabled: boolean,
  delta: number | null
): ClaudeCheckState {
  const [ab, setAb] = useState<ClaudeCheckAb | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ClaudeCheckState['error']>(null);

  useEffect(() => {
    if (!enabled || !token) {
      setAb(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = delta !== null ? `?delta=${delta}` : '';

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/claude-check${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!response.ok) {
          setError(response.status === 403 ? 'forbidden' : response.status === 404 ? 'unavailable' : 'failed');
          return;
        }
        const body = mapClaudeCheckAb(await response.json());
        if (!cancelled) setAb(body);
      } catch {
        if (!cancelled) setError('failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled, token, delta]);

  return { ab, loading, error };
}
